import React from 'react';
import { createTheme, Theme } from '@mui/material/styles';
import useProductsProvider from '../layouts/MainLayout/hooks/useProductsProvider';

export function useAppTheme(): Theme {
  const { currentProduct, colorThemes } = useProductsProvider();

  return React.useMemo(
    () =>
      createTheme({
        palette: {
          ...colorThemes[currentProduct],
          background: {
            default: '#ffffff',
            paper: '#f8f9fa',
          },
          text: {
            primary: '#333333',
            secondary: '#666666',
            disabled: '#999999',
          },
          grey: {
            100: '#f8f9fa',
            200: '#f0f0f0',
            300: '#e0e0e0',
            400: '#bdbdbd',
            500: '#9e9e9e',
            600: '#757575',
            700: '#616161',
            800: '#424242',
            900: '#212121',
          },
          error: {
            main: '#ff0000',
            light: '#ff000036',
            contrastText: '#ffffff',
          },
        },
        typography: {
          htmlFontSize: 18,
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          h4: {
            fontWeight: 'bold',
            color: '#333333',
          },
          h6: {
            fontWeight: 600,
            color: '#333333',
          },
          body1: {
            color: '#666666',
            fontSize: '1.1rem',
          },
          body2: {
            color: '#666666',
            fontSize: '1rem',
          },
          caption: {
            color: '#999999',
            fontSize: '0.85rem',
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: '25px',
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '0.8rem',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: '12px',
                border: '1px solid #e0e0e0',
                backgroundColor: '#f8f9fa',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                },
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: '25px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e0e0e0',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input': {
                  py: 2,
                  px: 2,
                  fontSize: '1rem',
                },
              },
            },
          },
          MuiAvatar: {
            styleOverrides: {
              root: {
                border: '4px solid #ffffff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              },
            },
          },
        },
      }),
    [currentProduct, colorThemes],
  );
}
